import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Check } from "lucide-react"

/** A setting that is on or off: label and explanation left, switch right. */
export function SettingsSwitchField({
  id,
  label,
  description,
  checked,
  disabled = false,
  onCheckedChange,
}: {
  id: string
  label: string
  description?: string
  checked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1">
        <Label htmlFor={id}>{label}</Label>
        {description && <Typography as="muted">{description}</Typography>}
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  )
}

/** Keeps a whole number inside the range the backend accepts; an empty
 *  field falls back to the low end. */
export function clampNumber(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min
  return Math.min(max, Math.max(min, Math.trunc(value)))
}

/**
 * A whole number with its unit ("days", "reminders") inside the field. Typing
 * is free; leaving the field snaps the value into `min`..`max`.
 */
export function SettingsNumberInput({
  id,
  label,
  value,
  min,
  max,
  unit,
  disabled = false,
  invalid = false,
  describedBy,
  onChange,
}: {
  id: string
  /** Accessible name, when no `<label>` points at `id`. */
  label?: string
  value: number
  min: number
  max: number
  unit?: string
  disabled?: boolean
  /** Marks the field (its border) as breaking a rule. */
  invalid?: boolean
  /** The id of the text that explains the rule. */
  describedBy?: string
  onChange: (value: number) => void
}) {
  return (
    <InputGroup className="w-48" data-disabled={disabled}>
      <InputGroupInput
        id={id}
        aria-label={label}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        type="number"
        min={min}
        max={max}
        disabled={disabled}
        value={Number.isNaN(value) ? "" : value}
        onChange={(event) => onChange(event.target.valueAsNumber)}
        onBlur={(event) =>
          onChange(clampNumber(event.target.valueAsNumber, min, max))
        }
      />
      {unit && (
        <InputGroupAddon align="inline-end">
          <InputGroupText>{unit}</InputGroupText>
        </InputGroupAddon>
      )}
    </InputGroup>
  )
}

export type ContentLanguage = "en" | "de"

const CONTENT_LANGUAGES: ContentLanguage[] = ["en", "de"]

/**
 * Switches which language's copy of a text is being edited. With
 * `needsWriting`, each language also says whether its copy is done ("EN ✓")
 * or still empty or a placeholder ("DE pending"), so the one not on screen
 * isn't forgotten.
 */
export function LanguageSwitch({
  value,
  onChange,
  needsWriting,
}: {
  value: ContentLanguage
  onChange: (language: ContentLanguage) => void
  needsWriting?: Record<ContentLanguage, boolean>
}) {
  const { translate } = useTranslation()
  const pending = translate("SETTINGS/SETUP/LANGUAGE_PENDING")
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      value={value}
      // Clicking the selected language again would clear the choice.
      onValueChange={(next) => next && onChange(next as ContentLanguage)}
      aria-label={translate("MC_TPL_EC_LANG_LABEL")}
    >
      {CONTENT_LANGUAGES.map((language) => {
        const code = language.toUpperCase()
        const todo = needsWriting?.[language]
        return (
          <ToggleGroupItem
            key={language}
            value={language}
            aria-label={todo ? `${code}, ${pending}` : code}
            // Sized to its content: "DE • pending" is wider than "EN ✓".
            className="flex-none gap-1.5 px-2.5"
          >
            {code}
            {todo === false && (
              <Check aria-hidden className="size-3.5 text-muted-foreground" />
            )}
            {todo && (
              <span
                aria-hidden
                className="flex items-center gap-1 text-xs font-normal text-muted-foreground"
              >
                <span className="size-1.5 rounded-full bg-warning" />
                {pending}
              </span>
            )}
          </ToggleGroupItem>
        )
      })}
    </ToggleGroup>
  )
}
